import React, { useState } from 'react';
import { MediaFileRead } from '../types/api';
import { apiService } from '../services/api';
import { urlParams } from '../utils/urlParams';
import { Download, Edit2, Trash2, Film, Image, File, X, Check, ExternalLink } from 'lucide-react';
import { useVideoThumbnail } from '../hooks/useVideoThumbnail';

interface MediaCardProps {
  media: MediaFileRead;
  onUpdate: (updatedMedia: MediaFileRead) => void;
  onDelete: (mediaId: string) => void;
}

export const MediaCard: React.FC<MediaCardProps> = ({ media, onUpdate, onDelete }) => {
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState(media.notes || '');
  const [isEditingFilename, setIsEditingFilename] = useState(false);
  const [editedFilename, setEditedFilename] = useState(media.ui_filename);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showFullView, setShowFullView] = useState(false);

  const isImage = media.content_type.startsWith('image/');
  const isVideo = media.content_type.startsWith('video/');

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDownload = () => {
    apiService.fetchMediaBlob(media.id)
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = media.original_filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Download failed:', error);
        alert('Download failed. Please try again.');
      });
  };

  const handleUpdateNotes = async () => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    try {
      const updatedMedia = await apiService.updateMediaFileNotes(media.id, editedNotes);
      onUpdate(updatedMedia);
      setIsEditingNotes(false);
    } catch (error) {
      console.error('Failed to update notes:', error);
      alert('Failed to update notes. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateFilename = async () => {
    if (isUpdating || !editedFilename.trim()) return;
    
    setIsUpdating(true);
    try {
      const updatedMedia = await apiService.updateMediaFileUIFilename(media.id, editedFilename.trim());
      onUpdate(updatedMedia);
      setIsEditingFilename(false);
    } catch (error) {
      console.error('Failed to update filename:', error);
      alert('Failed to update filename. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedNotes(media.notes || '');
    setIsEditingNotes(false);
  };

  const handleCancelFilenameEdit = () => {
    setEditedFilename(media.ui_filename);
    setIsEditingFilename(false);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this media file? This action cannot be undone.')) {
      try {
        await apiService.deleteMediaFile(media.id);
        onDelete(media.id);
      } catch (error) {
        console.error('Failed to delete media:', error);
        alert('Failed to delete media. Please try again.');
      }
    }
  };

  const getFileIcon = () => {
    if (isImage) return <Image size={20} className="text-green-600" />;
    if (isVideo) return <Film size={20} className="text-blue-600" />;
    return <File size={20} className="text-gray-600" />;
  };

  const handleNavigateToDetail = () => {
    urlParams.navigateToMediaDetail(media.id);
  };

  const MediaPreview = () => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const videoThumbnail = useVideoThumbnail(isVideo ? previewUrl : null);
    
    React.useEffect(() => {
      if (isImage || isVideo) {
        apiService.fetchMediaBlob(media.id)
          .then(blob => {
            const url = window.URL.createObjectURL(blob);
            setPreviewUrl(url);
          })
          .catch(error => {
            console.error('Failed to load media preview:', error);
          });
        
        return () => {
          if (previewUrl) {
            window.URL.revokeObjectURL(previewUrl);
          }
        };
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [media.id, isImage, isVideo, previewUrl]);

    if (isImage && previewUrl) {
      return (
        <img
          src={previewUrl}
          alt={media.ui_filename}
          className="w-full h-48 object-cover cursor-pointer"
          onClick={() => setShowFullView(true)}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      );
    }
    
    if (isVideo && videoThumbnail) {
      return (
        <div 
          className="relative w-full h-48 cursor-pointer group"
          onClick={handleNavigateToDetail}
        >
          <img
            src={videoThumbnail}
            alt={media.ui_filename}
            className="w-full h-48 object-cover"
          />
          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 group-hover:bg-opacity-30 transition-opacity">
            <div className="w-16 h-16 bg-white bg-opacity-95 rounded-full flex items-center justify-center shadow-lg">
              <svg className="w-8 h-8 text-gray-900 ml-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
              </svg>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="w-full h-48 bg-gray-100 flex items-center justify-center cursor-pointer" onClick={() => setShowFullView(true)}>
        <div className="text-center">
          {getFileIcon()}
          <p className="mt-2 text-sm text-gray-600 truncate px-2">{media.ui_filename}</p>
          {(isImage || isVideo) && !previewUrl && (
            <div className="mt-2 animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400 mx-auto"></div>
          )}
        </div>
      </div>
    );
  };

  const FullViewModal = () => {
    const [fullViewUrl, setFullViewUrl] = useState<string | null>(null);
    
    React.useEffect(() => {
      if (showFullView && (isImage || isVideo)) {
        const token = localStorage.getItem('access_token');
        const downloadUrl = apiService.getMediaFileDownloadUrl(media.id);
        
        fetch(downloadUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        .then(response => response.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          setFullViewUrl(url);
        })
        .catch(error => {
          console.error('Failed to load full media view:', error);
        });
        
        return () => {
          if (fullViewUrl) {
            window.URL.revokeObjectURL(fullViewUrl);
          }
        };
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showFullView, media.id, isImage, isVideo, fullViewUrl]);

    if (!showFullView) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
        <div className="relative max-w-4xl max-h-full w-full">
          <button
            onClick={() => setShowFullView(false)}
            className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-75"
          >
            <X size={24} />
          </button>
          
          {isImage && fullViewUrl && (
            <img
              src={fullViewUrl}
              alt={media.ui_filename}
              className="w-full h-auto max-h-full object-contain"
            />
          )}
          
          {isVideo && fullViewUrl && (
            <video
              src={fullViewUrl}
              className="w-full h-auto max-h-full object-contain"
              controls
              playsInline
              autoPlay
            />
          )}
          
          {!isImage && !isVideo && (
            <div className="bg-white rounded-lg p-8 text-center">
              <div className="text-6xl mb-4">{getFileIcon()}</div>
              <h3 className="text-xl font-semibold mb-2">{media.ui_filename}</h3>
              <p className="text-gray-600 mb-4">This file type cannot be previewed</p>
              <button
                onClick={handleDownload}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center mx-auto"
              >
                <Download size={16} className="mr-2" />
                Download
              </button>
            </div>
          )}
          
          {(isImage || isVideo) && !fullViewUrl && (
            <div className="bg-white rounded-lg p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading media...</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Media Preview */}
        <MediaPreview />
        
        {/* Content */}
        <div className="p-4">
          {/* File Info */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              {isEditingFilename ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={editedFilename}
                    onChange={(e) => setEditedFilename(e.target.value)}
                    placeholder="Enter filename..."
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength={255}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleUpdateFilename();
                      if (e.key === 'Escape') handleCancelFilenameEdit();
                    }}
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {editedFilename.length}/255 characters
                    </span>
                    <div className="flex space-x-2">
                      <button
                        onClick={handleCancelFilenameEdit}
                        disabled={isUpdating}
                        className="text-gray-600 hover:text-gray-800 p-1"
                        title="Cancel"
                      >
                        <X size={16} />
                      </button>
                      <button
                        onClick={handleUpdateFilename}
                        disabled={isUpdating || !editedFilename.trim()}
                        className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                        title="Save"
                      >
                        <Check size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 group">
                    <h3 className="text-sm font-medium text-gray-900 truncate" title={media.ui_filename}>
                      {media.ui_filename}
                    </h3>
                    <button
                      onClick={() => setIsEditingFilename(true)}
                      className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-600 transition-opacity"
                      title="Edit filename"
                    >
                      <Edit2 size={14} />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatFileSize(media.file_size)} • {formatDate(media.upload_date)}
                  </p>
                  {media.ui_filename !== media.original_filename && (
                    <p className="text-xs text-gray-400 mt-0.5" title="Original filename">
                      Original: {media.original_filename}
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center space-x-1 ml-2">
              {getFileIcon()}
            </div>
          </div>

          {/* Notes Section */}
          <div className="mb-3">
            {isEditingNotes ? (
              <div className="space-y-2">
                <textarea
                  value={editedNotes}
                  onChange={(e) => setEditedNotes(e.target.value)}
                  placeholder="Add notes about this media..."
                  className="w-full p-2 border border-gray-300 rounded-md text-sm resize-none"
                  rows={3}
                  maxLength={1000}
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {editedNotes.length}/1000 characters
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleCancelEdit}
                      disabled={isUpdating}
                      className="text-gray-600 hover:text-gray-800 p-1"
                    >
                      <X size={16} />
                    </button>
                    <button
                      onClick={handleUpdateNotes}
                      disabled={isUpdating}
                      className="text-green-600 hover:text-green-800 p-1"
                    >
                      <Check size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div 
                className="min-h-[60px] p-2 bg-gray-50 rounded border cursor-pointer hover:bg-gray-100"
                onClick={() => setIsEditingNotes(true)}
              >
                {media.notes ? (
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{media.notes}</p>
                ) : (
                  <p className="text-sm text-gray-500 italic">Click to add notes...</p>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between space-x-2">
            <div className="flex space-x-2">
              <button
                onClick={handleDownload}
                className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                <Download size={14} className="mr-1.5" />
                Download
              </button>
              <button
                onClick={handleNavigateToDetail}
                className="flex items-center px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                title="View in detail page"
              >
                <ExternalLink size={14} className="mr-1.5" />
                View Details
              </button>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => setIsEditingNotes(true)}
                className="p-1.5 text-gray-600 hover:text-blue-600 transition-colors"
                title="Edit notes"
              >
                <Edit2 size={16} />
              </button>
              <button
                onClick={handleDelete}
                className="p-1.5 text-gray-600 hover:text-red-600 transition-colors"
                title="Delete media"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <FullViewModal />
    </>
  );
};
